import { useState } from 'react';
import { ChevronDown, ChevronRight, MessageSquare, Calendar, Layers } from 'lucide-react';
import { Card, Loading } from '../common';
import { TarotCard } from '../tarot';
import type { TarotReading } from '../../types/tarot';

interface DeepDiveThreadProps {
  parentReading: TarotReading;
  deepDiveCount: number;
  onLoadDeepDives: (parentId: string) => Promise<TarotReading[]>;
  onViewReading: (reading: TarotReading) => void;
}

export function DeepDiveThread({
  parentReading,
  deepDiveCount,
  onLoadDeepDives,
  onViewReading
}: DeepDiveThreadProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [deepDives, setDeepDives] = useState<TarotReading[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggleExpand = async () => {
    if (!isExpanded && deepDives.length === 0) {
      // Load deep dives on first expand
      setIsLoading(true);
      try {
        const loadedDeepDives = await onLoadDeepDives(parentReading.id);
        setDeepDives(loadedDeepDives);
      } catch (error) {
        console.error('Failed to load deep dives:', error);
      } finally {
        setIsLoading(false);
      }
    }
    setIsExpanded(!isExpanded);
  };

  const formatRelativeDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown date';
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (deepDiveCount === 0) {
    return null;
  }

  return (
    <div className="mt-3 border-l-2 border-primary-500/30 pl-4">
      {/* Deep Dive Toggle Button */}
      <button
        onClick={handleToggleExpand}
        className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors group"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <Layers className="h-4 w-4" />
        <span className="font-medium">
          {deepDiveCount} Deep Dive{deepDiveCount > 1 ? 's' : ''}
        </span>
        {!isExpanded && (
          <span className="text-gray-500 text-xs group-hover:text-gray-400">
            Click to expand
          </span>
        )}
      </button>

      {/* Deep Dive List */}
      {isExpanded && (
        <div className="mt-3 space-y-2">
          {isLoading ? (
            <div className="py-4">
              <Loading size="sm" text="Loading deep dives..." />
            </div>
          ) : deepDives.length > 0 ? (
            deepDives.map((deepDive, index) => (
              <Card
                key={deepDive.id}
                className="bg-dark-750 hover:bg-dark-700 transition-colors cursor-pointer"
                padding="sm"
                onClick={() => onViewReading(deepDive)}
              >
                <div className="flex items-start gap-3">
                  {/* Deep Dive Number */}
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Question */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-white text-sm font-medium line-clamp-2">
                        <MessageSquare className="h-3 w-3 inline mr-1 text-primary-400" />
                        {deepDive.question}
                      </p>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs ${
                        deepDive.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : deepDive.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {deepDive.status}
                      </span>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatRelativeDate(deepDive.createdAt)}
                      </span>
                      <span>{deepDive.cards.length} card{deepDive.cards.length > 1 ? 's' : ''}</span>
                    </div>

                    {/* Card Preview */}
                    <div className="flex gap-1.5">
                      {deepDive.cards.slice(0, 3).map((card, cardIndex) => (
                        <div key={cardIndex} className="flex-shrink-0">
                          <TarotCard
                            card={card}
                            isRevealed={true}
                            size="xs"
                            showMeaning={false}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Interpretation Preview */}
                    {deepDive.interpretation && (
                      <div className="mt-2 p-2 bg-dark-800 rounded text-xs text-gray-300 line-clamp-2">
                        {deepDive.interpretation.length > 100 
                          ? `${deepDive.interpretation.substring(0, 100)}...`
                          : deepDive.interpretation
                        }
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="py-4 text-center text-gray-500 text-sm">
              No deep dives found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
